// Web-push CLIENT (feature #11, layer 3).
//
// Subscribes the installed PWA to free, carrier-unfilterable web push and
// registers the subscription with the BFF so the server can target this
// device. The VAPID public key is a non-secret build-time constant (config.js);
// the private half lives in Secret Manager and is read only by the BFF.

import axios from "axios";
import { BACKEND_URL, VAPID_PUBLIC_KEY } from "./config";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

// Returns the PushSubscription, or null if push isn't configured/permitted yet.
export async function subscribeToPush() {
  if (!pushSupported() || !VAPID_PUBLIC_KEY) return null;

  const permission = await Notification.requestPermission();
  if (permission !== "granted") return null;

  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing) return existing;

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
  });
}

// POST the subscription to the BFF under the authenticated employee.
async function sendSubscriptionToBackend(subscription, token) {
  await axios.post(
    `${BACKEND_URL}/push/portal/subscribe`,
    { subscription },
    { headers: { Authorization: `Bearer ${token}` } }
  );
}

// One-shot: subscribe this device and register it with the BFF. Best-effort —
// swallows errors so a denied permission or offline state never breaks the app.
// Callers pass the current auth token; no-op without one.
export async function registerPush(token) {
  if (!token || !pushSupported()) return;
  try {
    const subscription = await subscribeToPush();
    if (!subscription) return;
    await sendSubscriptionToBackend(subscription, token);
  } catch (_err) {
    // best-effort; nothing user-facing depends on push succeeding.
  }
}
